import { Col, Row, Tag, Tooltip, Select } from "antd";
import { Filter } from "../pages/Dashboard";
import StatusFilters from "./StatusFilters";
import { StatusCounts } from "../utils";
import TagInput from "./InputWithTags";
import DebounceSelect from "./DebounceSelect";
import { searchServiceTeams } from "../store/serviceTeamSlice";
import { DocumentTypeEnum } from "../enums/DocumentTypeEnum";
import { useMemo } from "react";

const { Option } = Select;

interface Props {
  statusCounts: StatusCounts[];
  filter: Filter;
  showServiceTeamDropdownSearch: boolean;
  showDocumentTypeDropdown: boolean;
  showStatusTypeDropdown: boolean;
  onFilterChange: (key: keyof Filter, value?: number | string) => void;
}

const SearchStyle = {
  display: "flex",
  flex: 1,
};

function SearchBar({
  statusCounts,
  filter,
  onFilterChange,
  showServiceTeamDropdownSearch,
  showDocumentTypeDropdown,
  showStatusTypeDropdown
}: Props) {
  const fetchServiceTeamList = async (criteria: string): Promise<any[]> => {
    return searchServiceTeams(criteria).then((res: any) => {
      return res;
    });
  };

  const filterByServiceTeam = (value: any) => {
    const newArr = serviceTeamTags;
    if (value && value.value) {
      if (!newArr.includes(value.value)) {
        newArr.push(value.value);
      }
    }
    onFilterChange("st", newArr.join(','));
  };

  const handleSearch = (value: string[]) => {
    if (value.length === 0) {
      onFilterChange("search", null);
    } else {
      onFilterChange("search", value.join(','));
    }
  };

  const handleDelete = (tag: string) => {
    const newArr = serviceTeamTags.filter(st => st !== tag);
    if (newArr.length === 0) {
      onFilterChange("st", null);
    } else {
      onFilterChange("st", newArr.join(','));
    }
  };

  const handleDocumentTypeChange = (value: DocumentTypeEnum) => {
    onFilterChange("docType", value);
  };

  const handleFileStatusChange = (value: string) => {
    onFilterChange("fileStatus", value)
  };

  const numDropdowns = (showServiceTeamDropdownSearch ? 1 : 0) + (showDocumentTypeDropdown ? 1 : 0) + (showStatusTypeDropdown ? 1 : 0);
  const searchTags = useMemo(() => filter?.search?.split(',') ?? [], [filter?.search]);
  const serviceTeamTags = useMemo(() => filter?.st?.split(',') ?? [], [filter?.st]);
  const filestatuses = useMemo(() => filter?.status?.split(',') ?? [], [filter?.status]);
  
  return (
    <div className="search-bar">
      <Row gutter={8}>
        <Col span={12} className="fw-800 fs-12px">
          Search
        </Col>
        <Col span={12} className="fw-800 fs-12px">
          Status
        </Col>
      </Row>
      <Row gutter={8} className="mt-5px">
        <Col span={12 - numDropdowns * 3}>
          <TagInput value={searchTags} onSearch={handleSearch} />
        </Col>
        {showServiceTeamDropdownSearch && (
          <Col span={3}>
            <Row>
              <Col span={24} className="mb-5px">
                <DebounceSelect
                  size="middle"
                  value={null}
                  placeholder="Service Team"
                  fetchOptions={fetchServiceTeamList}
                  onChange={filterByServiceTeam}
                  style={SearchStyle}
                />
              </Col>
              <Col span={24}>
                {serviceTeamTags.map((item) =>
                  item.length > 20 ? (
                    <Tooltip title={item} key={item}>
                      <Tag className="m-3px" closable onClose={() => handleDelete(item)}>{`${item.slice(0, 20)}...`}</Tag>
                    </Tooltip>
                  ) : (
                    <Tag className="m-3px" closable key={item} onClose={() => handleDelete(item)}>
                      {item}
                    </Tag>
                  )
                )}
              </Col>
            </Row>
          </Col>
        )}
        {showDocumentTypeDropdown && (
            <Col span={3}>
            <Select
              placeholder="Document Type"
              onChange={handleDocumentTypeChange}
              style={{ width: "100%" }}
              value={filter?.docType || ""}
              defaultValue={""}
            >
              <Option value={DocumentTypeEnum.Invoice}>PO</Option>
              <Option value={DocumentTypeEnum.StockInvoice}>Stock PO</Option>
              <Option value={DocumentTypeEnum.Expense}>Expense</Option>
              <Option value={DocumentTypeEnum.MultiPage}>Multi-page</Option>
              <Option value={DocumentTypeEnum.None}>None</Option>
              <Option value={""}>All</Option>
            </Select>
            </Col>
        )}
        {showStatusTypeDropdown && (
          <Col span={3}>
            <Select
              placeholder="Document Status"
              onChange={handleFileStatusChange}
              style={{ width: "100%" }}
              value={filter?.fileStatus || (filestatuses.length === 1 ? filestatuses[0] : "")}
              defaultValue={""}
            >
              {filestatuses.map((item) =>
                <Option value={item} key={item}>{item}</Option>
              )}
              {filestatuses.length > 1 && <Option value={""}>All</Option>}
            </Select>
          </Col>
        )}
        <Col span={12} offset={showServiceTeamDropdownSearch && showDocumentTypeDropdown ? 0 : showServiceTeamDropdownSearch || showDocumentTypeDropdown ? 3 : 4}>
          <StatusFilters statusCounts={statusCounts} filter={filter} onFilterChange={onFilterChange} />
        </Col>
      </Row>
    </div>
  );
}

export default SearchBar;